// ABOUTME: Tour context for managing first-use application tour state.
// ABOUTME: Handles tour progress, completion tracking, and interactive actions.

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { userApi, investigationApi } from '@/services/api';
import { useAuth } from './AuthContext';
import { useApp } from './AppContext';
import { tourSteps, getStepIndex } from '@/components/tour/tourSteps';

// Steps at or after this index require ephemeral demo state (insight, pin) that isn't persisted
// If user refreshes past this point, we must restart from the beginning
const FIRST_EPHEMERAL_STEP_INDEX = getStepIndex('click-send');

interface TourContextType {
  // State
  isRunning: boolean;
  stepIndex: number;
  hasCompletedTour: boolean;
  isLoading: boolean;
  isWaitingForAction: boolean;
  currentAction: string | null;

  // Actions
  startTour: () => void;
  endTour: () => void;
  skipTour: () => Promise<void>;
  completeTour: () => Promise<void>;
  restartTour: () => void;
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (index: number) => void;

  // Interactive tour actions
  onInvestigationCreated: (investigationId: string) => void;
  onMessageSent: () => void;
  onResponseComplete: () => void;
  onInsightPinned: () => void;
  onReportsNavClicked: () => void;
  onNewReportClicked: () => void;

  // Demo state
  tourInvestigationId: string | null;
  tourMessage: string | null;
  useDemoInsight: boolean; // True when tour should use fake demo insight instead of real API
  setTourPinId: (pinId: string | null) => void; // Track the demo pin for cleanup
}

const TourContext = createContext<TourContextType | undefined>(undefined);

const TOUR_LOCAL_STORAGE_KEY = 'obsidian_tour_state';
const TOUR_DEMO_MESSAGE = 'Show me a quick table about my data';

interface TourProviderProps {
  children: ReactNode;
}

export function TourProvider({ children }: TourProviderProps) {
  const { user, isAuthenticated } = useAuth();
  const { unpinItem, deleteSession, refreshSessions } = useApp();
  const navigate = useNavigate();
  const location = useLocation();

  // Tour state
  const [isRunning, setIsRunning] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [hasCompletedTour, setHasCompletedTour] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isWaitingForAction, setIsWaitingForAction] = useState(false);
  const [currentAction, setCurrentAction] = useState<string | null>(null);

  // Demo investigation state
  const [tourInvestigationId, setTourInvestigationId] = useState<string | null>(null);
  const [tourMessage, setTourMessage] = useState<string | null>(null);
  const [useDemoInsight, setUseDemoInsight] = useState(false);
  // Track pin created during tour for cleanup
  const [tourPinId, setTourPinId] = useState<string | null>(null);

  // Load tour state from API on mount
  useEffect(() => {
    const loadTourState = async () => {
      if (!isAuthenticated || !user) {
        setIsLoading(false);
        return;
      }

      try {
        const profile = await userApi.me();
        const tourCompleted = profile.preferences?.tour_completed ?? false;
        setHasCompletedTour(tourCompleted);

        // Check localStorage for in-progress tour
        const savedState = localStorage.getItem(TOUR_LOCAL_STORAGE_KEY);
        if (savedState) {
          try {
            const parsed = JSON.parse(savedState);
            if (parsed.isRunning && !tourCompleted) {
              const savedStepIndex = parsed.stepIndex || 0;

              // If we were past the ephemeral steps (demo insight/pin), we've lost that state on refresh
              // Restart from the beginning and clean up orphaned investigation
              if (savedStepIndex >= FIRST_EPHEMERAL_STEP_INDEX) {
                console.log('[TourContext] Page was refreshed mid-tour past ephemeral steps, restarting from beginning');

                // Clean up orphaned investigation if it exists
                if (parsed.tourInvestigationId) {
                  console.log('[TourContext] Cleaning up orphaned tour investigation:', parsed.tourInvestigationId);
                  deleteSession(parsed.tourInvestigationId).catch((err) => {
                    console.error('[TourContext] Failed to delete orphaned investigation:', err);
                  });
                  refreshSessions();
                }

                // Restart from beginning
                setIsRunning(true);
                setStepIndex(0);
                localStorage.removeItem(TOUR_LOCAL_STORAGE_KEY);
              } else {
                // Safe to resume from saved step (no ephemeral state needed)
                setIsRunning(true);
                setStepIndex(savedStepIndex);
                if (parsed.tourInvestigationId) {
                  setTourInvestigationId(parsed.tourInvestigationId);
                }
              }
            }
          } catch {
            localStorage.removeItem(TOUR_LOCAL_STORAGE_KEY);
          }
        }

        // Auto-start tour for new users who haven't completed it
        if (!tourCompleted && !savedState) {
          setTimeout(() => {
            setIsRunning(true);
            setStepIndex(0);
          }, 1000);
        }
      } catch (error) {
        console.error('[TourContext] Failed to load tour state:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadTourState();
  }, [isAuthenticated, user]);

  // Persist tour state to localStorage when running
  useEffect(() => {
    if (isRunning) {
      localStorage.setItem(
        TOUR_LOCAL_STORAGE_KEY,
        JSON.stringify({ isRunning, stepIndex, tourInvestigationId })
      );
    }
  }, [isRunning, stepIndex, tourInvestigationId]);

  // Update waiting state based on current step
  useEffect(() => {
    if (isRunning && stepIndex < tourSteps.length) {
      const currentStep = tourSteps[stepIndex];
      if (currentStep.waitForAction) {
        setIsWaitingForAction(true);
        setCurrentAction(currentStep.waitForAction);
      } else {
        setIsWaitingForAction(false);
        setCurrentAction(null);
      }
    }
  }, [isRunning, stepIndex]);

  const startTour = useCallback(() => {
    setStepIndex(0);
    setIsRunning(true);
    setTourInvestigationId(null);
    setTourMessage(null);
    if (location.pathname !== '/') {
      navigate('/');
    }
  }, [navigate, location.pathname]);

  // Cleanup tour demo data (investigation and pin)
  const cleanupTourData = useCallback(async () => {
    console.log('[TourContext] Cleaning up tour data...');

    // Delete demo pin if created (from local AppContext state)
    if (tourPinId) {
      console.log('[TourContext] Removing tour pin from local state:', tourPinId);
      // unpinItem removes from local state and tries API delete (which may fail for demo pins - that's OK)
      unpinItem(tourPinId);
    }

    // Delete demo investigation if created (from server) - WITH VERIFICATION
    if (tourInvestigationId) {
      try {
        console.log('[TourContext] Verifying tour investigation before deletion:', tourInvestigationId);

        // SAFETY CHECK: Verify the investigation is actually a tour demo
        const response = await investigationApi.get(tourInvestigationId);

        if (response.investigation.metadata?.is_tour_demo !== true) {
          console.error(
            '[TourContext] SAFETY CHECK FAILED: Investigation is not marked as tour demo!',
            {
              investigationId: tourInvestigationId,
              title: response.investigation.title,
              is_tour_demo: response.investigation.metadata?.is_tour_demo,
            }
          );

          // TODO: Send to error tracking (Sentry)
          // Sentry.captureMessage('Tour cleanup safety check failed', {
          //   level: 'error',
          //   extra: { investigationId: tourInvestigationId, ...response.investigation },
          // });

          // Do NOT delete - this is likely a real user investigation
          return;
        }

        // Verification passed - safe to delete
        console.log('[TourContext] Verification passed, deleting tour investigation:', tourInvestigationId);
        await deleteSession(tourInvestigationId);

        // Refresh sessions list to remove the deleted investigation from sidebar
        await refreshSessions();
      } catch (err) {
        console.error('[TourContext] Failed to verify/delete tour investigation:', err);
        // If verification fails (e.g., investigation not found), that's OK
        // The investigation might have already been deleted or never existed
      }
    }

    console.log('[TourContext] Tour data cleanup complete');
  }, [tourPinId, tourInvestigationId, unpinItem, deleteSession, refreshSessions]);

  const endTour = useCallback(() => {
    setIsRunning(false);
    setIsWaitingForAction(false);
    setCurrentAction(null);
    setTourInvestigationId(null);
    setTourMessage(null);
    setUseDemoInsight(false);
    setTourPinId(null);
    localStorage.removeItem(TOUR_LOCAL_STORAGE_KEY);
  }, []);

  const skipTour = useCallback(async () => {
    // Clean up tour demo data first
    await cleanupTourData();

    setIsRunning(false);
    setIsWaitingForAction(false);
    setCurrentAction(null);
    setTourInvestigationId(null);
    setTourMessage(null);
    setUseDemoInsight(false);
    setTourPinId(null);
    localStorage.removeItem(TOUR_LOCAL_STORAGE_KEY);

    try {
      await userApi.updatePreferences({ tour_completed: true });
      setHasCompletedTour(true);
    } catch (error) {
      console.error('[TourContext] Failed to save tour skip:', error);
    }

    // Navigate to home after cleanup
    navigate('/');
  }, [cleanupTourData, navigate]);

  const completeTour = useCallback(async () => {
    // Clean up tour demo data first
    await cleanupTourData();

    setIsRunning(false);
    setIsWaitingForAction(false);
    setCurrentAction(null);
    setTourInvestigationId(null);
    setTourMessage(null);
    setUseDemoInsight(false);
    setTourPinId(null);
    localStorage.removeItem(TOUR_LOCAL_STORAGE_KEY);

    try {
      await userApi.updatePreferences({ tour_completed: true });
      setHasCompletedTour(true);
    } catch (error) {
      console.error('[TourContext] Failed to save tour completion:', error);
    }

    // Navigate to home after cleanup
    navigate('/');
  }, [cleanupTourData, navigate]);

  const restartTour = useCallback(() => {
    setHasCompletedTour(false);
    setStepIndex(0);
    setIsRunning(true);
    setTourInvestigationId(null);
    setTourMessage(null);
    setUseDemoInsight(false);
    setTourPinId(null);
    localStorage.removeItem(TOUR_LOCAL_STORAGE_KEY);
    if (location.pathname !== '/') {
      navigate('/');
    }
  }, [navigate, location.pathname]);

  const nextStep = useCallback(() => {
    setStepIndex((prev) => prev + 1);
  }, []);

  const prevStep = useCallback(() => {
    setStepIndex((prev) => Math.max(0, prev - 1));
  }, []);

  const goToStep = useCallback((index: number) => {
    setStepIndex(index);
  }, []);

  // Interactive action handlers
  const onInvestigationCreated = useCallback((investigationId: string) => {
    console.log('[TourContext] Investigation created:', investigationId);
    if (isRunning && currentAction === 'click-new-investigation') {
      setTourInvestigationId(investigationId);
      // Move to click-send step and set the demo message to populate input
      const clickSendIndex = getStepIndex('click-send');
      if (clickSendIndex !== -1) {
        // Small delay to let navigation complete
        setTimeout(() => {
          setTourMessage(TOUR_DEMO_MESSAGE);
          setStepIndex(clickSendIndex);
        }, 500);
      }
    }
  }, [isRunning, currentAction]);

  const onMessageSent = useCallback(() => {
    console.log('[TourContext] Message sent (click-send)');
    if (isRunning && currentAction === 'click-send') {
      // Enable demo insight mode for the tour
      setUseDemoInsight(true);
      setTourMessage(null);
      // Move to response-received step (user clicks Next to see insight explanation)
      const responseIndex = getStepIndex('response-received');
      if (responseIndex !== -1) {
        // Small delay to let the insight render
        setTimeout(() => {
          setStepIndex(responseIndex);
        }, 100);
      }
    }
  }, [isRunning, currentAction]);

  const onResponseComplete = useCallback(() => {
    console.log('[TourContext] Response complete, isRunning:', isRunning, 'currentAction:', currentAction);
    // Only proceed if tour is running - caller is responsible for timing
    if (!isRunning) {
      console.log('[TourContext] Tour not running, ignoring response complete');
      return;
    }

    // Move to click-pin step
    const pinIndex = getStepIndex('click-pin');
    const pinboardIndex = getStepIndex('pinboard-toggle');
    if (pinIndex !== -1) {
      let attempts = 0;
      const maxAttempts = 15; // 3 seconds max wait (15 * 200ms)

      // Wait for the pin button to appear in the DOM
      const waitForPinButton = () => {
        attempts++;
        const pinButton = document.querySelector('[data-tour="pin-button"]');
        if (pinButton) {
          console.log('[TourContext] Pin button found, advancing to click-pin step');
          setStepIndex(pinIndex);
        } else if (attempts < maxAttempts) {
          console.log('[TourContext] Pin button not found, retrying...', attempts);
          setTimeout(waitForPinButton, 200);
        } else {
          // Fallback: skip to pinboard step if pin button never appears
          console.log('[TourContext] Pin button not found after max attempts, skipping to pinboard');
          if (pinboardIndex !== -1) {
            setStepIndex(pinboardIndex);
          }
        }
      };
      // Start checking after a brief delay for the UI to update
      setTimeout(waitForPinButton, 500);
    }
  }, [isRunning, currentAction]);

  const onInsightPinned = useCallback(() => {
    console.log('[TourContext] Insight pinned, isRunning:', isRunning, 'currentAction:', currentAction);
    if (!isRunning) {
      console.log('[TourContext] Tour not running, ignoring insight pinned');
      return;
    }
    // Move to pinboard-toggle step with small delay for UI to update
    const pinboardIndex = getStepIndex('pinboard-toggle');
    console.log('[TourContext] Advancing to pinboard-toggle step at index:', pinboardIndex);
    if (pinboardIndex !== -1) {
      setTimeout(() => {
        setStepIndex(pinboardIndex);
      }, 300);
    }
  }, [isRunning, currentAction]);

  const onReportsNavClicked = useCallback(() => {
    console.log('[TourContext] Reports nav clicked');
    if (isRunning && currentAction === 'click-reports-nav') {
      // Move to click-new-report step
      const newReportIndex = getStepIndex('click-new-report');
      if (newReportIndex !== -1) {
        // Small delay to let navigation complete
        setTimeout(() => {
          setStepIndex(newReportIndex);
        }, 300);
      }
    }
  }, [isRunning, currentAction]);

  const onNewReportClicked = useCallback(() => {
    console.log('[TourContext] New report clicked');
    if (isRunning && currentAction === 'click-new-report') {
      // Move to report-explain-investigation step (explains what investigations are for)
      const selectionIndex = getStepIndex('report-explain-investigation');
      if (selectionIndex !== -1) {
        let attempts = 0;
        const maxAttempts = 20; // 4 seconds max wait (20 * 200ms)

        // Wait for the wizard investigations element to appear (after API loads)
        const waitForWizard = () => {
          attempts++;
          const wizardEl = document.querySelector('[data-tour="wizard-investigations"]');
          if (wizardEl) {
            console.log('[TourContext] Wizard investigations element found, advancing to step');
            setStepIndex(selectionIndex);
          } else if (attempts < maxAttempts) {
            console.log('[TourContext] Wizard element not found, retrying...', attempts);
            setTimeout(waitForWizard, 200);
          } else {
            // Fallback: advance anyway after max attempts
            console.log('[TourContext] Wizard element not found after max attempts, advancing anyway');
            setStepIndex(selectionIndex);
          }
        };
        // Start checking after initial navigation delay
        setTimeout(waitForWizard, 300);
      }
    }
  }, [isRunning, currentAction]);

  const value: TourContextType = {
    isRunning,
    stepIndex,
    hasCompletedTour,
    isLoading,
    isWaitingForAction,
    currentAction,
    startTour,
    endTour,
    skipTour,
    completeTour,
    restartTour,
    nextStep,
    prevStep,
    goToStep,
    onInvestigationCreated,
    onMessageSent,
    onResponseComplete,
    onInsightPinned,
    onReportsNavClicked,
    onNewReportClicked,
    tourInvestigationId,
    tourMessage,
    useDemoInsight,
    setTourPinId,
  };

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}

export function useTour(): TourContextType {
  const context = useContext(TourContext);
  if (context === undefined) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
}
